import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import CreatableSelect from "react-select/creatable"
import PhoneInput, { CountryData } from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import {
  addGuest,
  getContacts,
  getContactsOnEvent,
  importFromContacts,
  setLoading,
} from "../../../redux/slices/contacts/contactsSlices"
import ChooseFileImport from "../../../components/Event/ImportGuest/ChooseFileImport"
import * as XLSX from "xlsx"
import * as FileSaver from "file-saver"
import Drawer from "./Drawer"
import styles from "../EventDashboardV2.module.scss"
import { buildSuffixOptions } from "../constants/suffixOptions"

type AddTab = "manual" | "import" | "contacts"

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 38,
    fontSize: 14,
    borderColor: state.isFocused ? "#a0a8b8" : "#e5e7eb",
    borderRadius: 6,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0,0,0,0.04)" : "none",
    "&:hover": { borderColor: "#a0a8b8" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 12px" }),
  indicatorSeparator: () => ({ display: "none" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? "#f3f4f6" : state.isFocused ? "#f9fafb" : "white",
    color: "#1a1a1a",
    "&:active": { backgroundColor: "#f3f4f6" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }),
}

const GULF_COUNTRIES = ["sa", "ae", "bh", "kw", "qa", "om"]

const toE164 = (digits: string) => (digits ? "+" + digits : "")

const EMPTY_GUEST = {
  suffix: "",
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  zone: "",
  quantity_members: 0,
}

const TEMPLATE_COLUMNS = [
  { first_name: "", last_name: "", suffix: "", phone: "", email: "", zone: "", quantity_members: 0 },
]

interface AddGuestsDrawerProps {
  show: boolean
  onClose: () => void
  filter: any
  notify: (text: string, isError: boolean) => void
}

const AddGuestsDrawer: React.FC<AddGuestsDrawerProps> = ({
  show,
  onClose,
  filter,
  notify,
}) => {
  const dispatch = useAppDispatch()
  const { event_id } = useParams()
  const { t, i18n } = useTranslation()
  const suffixOptions = useMemo(() => buildSuffixOptions(t), [t])

  const [activeTab, setActiveTab] = useState<AddTab>("manual")

  // ---- Manual tab state ----
  const [guest, setGuest] = useState({ ...EMPTY_GUEST, event_id: Number(event_id) })
  const [validation, setValidation] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  // Stripe-style stacked feedback: a transient toast near the top of the form,
  // a button flash, an auto-focus return, and a persistent "N added" chip in
  // the footer. Tracked together so every add produces a visible change at
  // whichever part of the screen the user is looking at.
  const [lastAdded, setLastAdded] = useState<{ name: string; id: number } | null>(null)
  const [sessionCount, setSessionCount] = useState(0)
  const [justAddedFlash, setJustAddedFlash] = useState(false)
  const successTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const firstNameRef = useRef<HTMLInputElement | null>(null)

  // ---- Import tab state ----
  const [file, setFile] = useState<any>(null)
  const [importProgress, setImportProgress] = useState(0)
  const importDone = importProgress >= 100

  // ---- Contacts tab state ----
  const allContacts = useAppSelector((s) => s.contacts.data)
  const eventGuests = useAppSelector((s) => s.contacts.data_on_event)
  const [contactSearch, setContactSearch] = useState("")
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set())
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [addingContacts, setAddingContacts] = useState(false)

  useEffect(() => {
    if (show) {
      resetManualForm()
      setFile(null)
      setImportProgress(0)
      setSelectedContactIds(new Set())
      setContactSearch("")
      setLastAdded(null)
      setSessionCount(0)
    }
    return () => {
      if (successTimer.current) clearTimeout(successTimer.current)
      if (flashTimer.current) clearTimeout(flashTimer.current)
    }
  }, [show])

  useEffect(() => {
    if (show && activeTab === "contacts") {
      setLoadingContacts(true)
      dispatch(getContacts({ sort: "", searchValue: "", order: "" })).finally(() =>
        setLoadingContacts(false),
      )
    }
  }, [show, activeTab])

  // ---- Manual helpers ----
  const resetManualForm = () => {
    setGuest({ ...EMPTY_GUEST, event_id: Number(event_id) })
    setValidation({})
    setSaving(false)
    setLastAdded(null)
    setJustAddedFlash(false)
  }

  const handleInput = (field: string, value: any) => {
    setGuest((prev) => ({ ...prev, [field]: value }))
    if (validation[field]) {
      setValidation((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handlePhoneChange = (value: string, _data: CountryData | {}) => {
    handleInput("phone", value)
  }

  const validatePhone = (value: string): string | null => {
    const e164 = toE164(value)
    if (!value) return t("eventDashboardV2.validation.phoneRequired")
    const lengthResult = validatePhoneNumberLength(e164)
    if (lengthResult === "TOO_SHORT") return t("eventDashboardV2.validation.phoneIncomplete")
    if (lengthResult === "TOO_LONG") return t("eventDashboardV2.validation.phoneTooLong")
    if (lengthResult === "NOT_A_NUMBER") return t("eventDashboardV2.validation.phoneInvalid")
    const parsed = parsePhoneNumberFromString(e164)
    if (!parsed) return t("eventDashboardV2.validation.phoneInvalid")
    if (!parsed.isValid()) {
      const country = parsed.country
      return country
        ? t("eventDashboardV2.validation.phoneInvalidForCountry", { country: new Intl.DisplayNames([i18n.language === "ar" ? "ar" : "en"], { type: "region" }).of(country) })
        : t("eventDashboardV2.validation.phoneInvalid")
    }
    return null
  }

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}
    if (!String(guest.first_name).trim()) errors.first_name = t("eventDashboardV2.validation.firstNameRequired")
    const email = String(guest.email || "").trim()
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = t("eventDashboardV2.validation.emailInvalid")
    const phoneError = validatePhone(String(guest.phone || ""))
    if (phoneError) errors.phone = phoneError
    const qty = Number(guest.quantity_members)
    if (isNaN(qty) || qty < 0 || qty > 10) errors.quantity_members = t("eventDashboardV2.validation.additionalInvitesRange")
    return errors
  }

  const extractErrorMessage = (payload: any, fallback: string) => {
    if (!payload) return fallback
    if (typeof payload === "string") return payload
    if (typeof payload.message === "string") return payload.message
    if (typeof payload.error === "string") return payload.error
    const errors = payload.errors || payload
    if (errors && typeof errors === "object") {
      const first = Object.values(errors)[0] as any
      if (Array.isArray(first)) return String(first[0])
      if (typeof first === "string") return first
    }
    return fallback
  }

  const getAddGuestFailure = (payload: any) => {
    if (!payload) return null
    if (typeof payload === "string") return payload
    if (
      payload.errors ||
      payload.error ||
      payload.success === false ||
      payload.status === false ||
      payload.status_code >= 400
    ) {
      return payload
    }
    if (typeof payload.message === "string" && !payload.contact_id && !payload.data) {
      return payload
    }
    return null
  }

  const handleAddGuest = () => {
    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setValidation(errors)
      return
    }

    setSaving(true)
    dispatch(addGuest(guest))
      .then((result: any) => {
        const failurePayload = result.error ? result.payload : getAddGuestFailure(result.payload)
        if (failurePayload) {
          const validationErrors = failurePayload?.errors || (
            failurePayload &&
            typeof failurePayload === "object" &&
            !failurePayload.message &&
            !failurePayload.error
              ? failurePayload
              : null
          )
          if (validationErrors && typeof validationErrors === "object") {
            setValidation(validationErrors)
            notify(extractErrorMessage(validationErrors, t("eventDashboardV2.messages.failedAddGuest")), true)
          } else {
            notify(extractErrorMessage(failurePayload, t("eventDashboardV2.messages.failedAddGuest")), true)
          }
          setSaving(false)
          return
        }

        if (result.error) {
          const validationErrors = result.payload?.errors || (
            result.payload &&
            typeof result.payload === "object" &&
            !result.payload.message &&
            !result.payload.error
              ? result.payload
              : null
          )
          if (validationErrors && typeof validationErrors === "object") {
            setValidation(validationErrors)
            notify(extractErrorMessage(validationErrors, t("eventDashboardV2.messages.failedAddGuest")), true)
          } else {
            notify(extractErrorMessage(result.payload, t("eventDashboardV2.messages.failedAddGuest")), true)
          }
          setSaving(false)
          return
        }

        dispatch(setLoading())
        dispatch(getContactsOnEvent(filter))

        // Celebrate, clear, and return the user to the first field so they
        // can keep entering guests without touching the mouse. The toast,
        // button flash, and session counter all update together.
        const displayName =
          [guest.suffix, guest.first_name, guest.last_name].filter(Boolean).join(" ") ||
          t("eventDashboardV2.labels.guest")
        // Bumping the id forces the toast to re-mount and replay its entrance
        // animation when adds happen in quick succession with the same name.
        setLastAdded({ name: displayName, id: Date.now() })
        setSessionCount((n) => n + 1)
        setJustAddedFlash(true)

        if (successTimer.current) clearTimeout(successTimer.current)
        successTimer.current = setTimeout(() => setLastAdded(null), 3200)

        if (flashTimer.current) clearTimeout(flashTimer.current)
        flashTimer.current = setTimeout(() => setJustAddedFlash(false), 1100)

        setGuest({ ...EMPTY_GUEST, event_id: Number(event_id) })
        setValidation({})
        setSaving(false)
        window.setTimeout(() => firstNameRef.current?.focus(), 0)
      })
      .catch(() => {
        setSaving(false)
        notify(t("eventDashboardV2.messages.somethingWentWrongTryAgain"), true)
      })
  }

  // ---- Import helpers ----
  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(TEMPLATE_COLUMNS)
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] }
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" })
    const blob = new Blob([buf], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    })
    FileSaver.saveAs(blob, "import_guests_template.xlsx")
  }

  const handleImportDone = () => {
    dispatch(setLoading())
    dispatch(getContactsOnEvent(filter))
    onClose()
  }

  // ---- Contacts helpers ----
  const stripDigits = (p: string) => (p || "").replace(/\D/g, "")

  const eventPhones = React.useMemo(() => {
    const set = new Set<string>()
    for (const g of eventGuests) {
      const d = stripDigits(g.phone)
      if (d) set.add(d)
    }
    return set
  }, [eventGuests])

  const isOnEvent = useCallback(
    (contact: any) => {
      const d = stripDigits(contact.phone)
      return d ? eventPhones.has(d) : false
    },
    [eventPhones],
  )

  const filteredContacts = allContacts.filter((c: any) => {
    if (!contactSearch) return true
    const q = contactSearch.toLowerCase()
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase()
    return name.includes(q) || (c.phone || "").includes(q) || (c.email || "").toLowerCase().includes(q)
  })

  const selectableContacts = filteredContacts.filter((c: any) => !isOnEvent(c))

  const toggleContact = useCallback(
    (id: number) => {
      setSelectedContactIds((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
    },
    [],
  )

  const toggleAllContacts = useCallback(() => {
    setSelectedContactIds((prev) => {
      if (prev.size === selectableContacts.length && selectableContacts.length > 0) {
        return new Set()
      }
      return new Set(selectableContacts.map((c: any) => c.id))
    })
  }, [selectableContacts])

  const handleAddFromContacts = () => {
    if (selectedContactIds.size === 0) return
    const contactsArr = Array.from(selectedContactIds).map((id) => ({
      contact_id: id,
      quantity_members: 0,
    }))

    setAddingContacts(true)
    dispatch(importFromContacts({ event_id: Number(event_id), contactsArr }))
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.addContactsFailed"), true)
          setAddingContacts(false)
          return
        }
        dispatch(setLoading())
        dispatch(getContactsOnEvent(filter))
        notify(t("eventDashboardV2.messages.contactAdded", { count: selectedContactIds.size }), false)
        setAddingContacts(false)
        onClose()
      })
      .catch(() => {
        setAddingContacts(false)
        notify(t("eventDashboardV2.messages.somethingWentWrongTryAgain"), true)
      })
  }

  // ---- Footer ----
  const renderFooter = () => {
    if (activeTab === "manual") {
      return (
        <div className={styles.drawerFooterActions}>
          {sessionCount > 0 && (
            <span
              className={`${styles.addGuestsSessionChip} ${styles.addGuestsSessionChipFooter} ${justAddedFlash ? styles.addGuestsSessionChipFlash : ""}`}
              aria-label={t("eventDashboardV2.messages.addedInSession", { count: sessionCount })}
            >
              <span className={styles.addGuestsSessionDot} />
              {t("eventDashboardV2.messages.addedInSession", { count: sessionCount })}
            </span>
          )}
          <button type="button" className={styles.drawerBtnCancel} onClick={onClose}>
            {sessionCount > 0 ? t("eventDashboardV2.actions.done") : t("eventDashboardV2.actions.cancel")}
          </button>
          <button
            type="button"
            className={`${styles.drawerBtnSave} ${justAddedFlash ? styles.drawerBtnSaveSuccess : ""}`}
            disabled={saving}
            onClick={handleAddGuest}
          >
            {saving ? (
              <>
                <SpinnerIcon /> {t("eventDashboardV2.messages.adding")}
              </>
            ) : justAddedFlash ? (
              <>
                <SuccessIcon /> {t("eventDashboardV2.messages.guestAdded")}
              </>
            ) : sessionCount > 0 ? (
              t("eventDashboardV2.actions.addAnother")
            ) : (
              t("eventDashboardV2.actions.addGuests")
            )}
          </button>
        </div>
      )
    }

    if (activeTab === "import") {
      if (importDone) {
        return (
          <div className={styles.drawerFooterActions}>
            <button type="button" className={styles.drawerBtnSave} onClick={handleImportDone}>
              {t("eventDashboardV2.actions.done")}
            </button>
          </div>
        )
      }
      return null
    }

    if (activeTab === "contacts") {
      const count = selectedContactIds.size
      return (
        <div className={styles.drawerFooterActions}>
          <button type="button" className={styles.drawerBtnCancel} onClick={onClose}>
            {t("eventDashboardV2.actions.cancel")}
          </button>
          <button
            type="button"
            className={styles.drawerBtnSave}
            disabled={addingContacts || count === 0}
            onClick={handleAddFromContacts}
          >
            {addingContacts
              ? t("eventDashboardV2.messages.adding")
              : count > 0
                ? t("eventDashboardV2.messages.contactAdded", { count })
                : t("eventDashboardV2.actions.selectContacts")}
          </button>
        </div>
      )
    }
    return null
  }

  const getInitials = (contact: any) => {
    const f = String(contact.first_name || "").charAt(0)
    const l = String(contact.last_name || "").charAt(0)
    return (f + l).toUpperCase() || "?"
  }

  const formatContactPhone = (phone: string) => {
    if (!phone) return ""
    const e164 = toE164(phone)
    const parsed = parsePhoneNumberFromString(e164)
    return parsed ? parsed.formatInternational() : "+" + phone
  }

  const selCount = selectedContactIds.size

  return (
    <Drawer open={show} onClose={onClose} title={t("eventDashboardV2.actions.addGuests")} footer={renderFooter()}>
      {/* Segmented control */}
      <div className={styles.addGuestsSegmented}>
        {(["manual", "import", "contacts"] as AddTab[]).map((tab) => (
          <button
            key={tab}
            className={`${styles.addGuestsSegBtn} ${activeTab === tab ? styles.addGuestsSegBtnActive : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "manual" && <><PenIcon /> {t("eventDashboardV2.messages.manual")}</>}
            {tab === "import" && <><UploadIcon /> {t("eventDashboardV2.messages.importFile")}</>}
            {tab === "contacts" && <><BookIcon /> {t("eventDashboardV2.actions.selectContacts")}</>}
          </button>
        ))}
      </div>

      {/* ---- Manual Tab ---- */}
      {activeTab === "manual" && (
        <div className={styles.addGuestsContent}>
          {/* Transient toast — re-mounts on every add via `id` key so repeated
              saves retrigger the slide-in animation. */}
          {lastAdded && (
            <div
              key={lastAdded.id}
              className={styles.addGuestsToast}
              role="status"
              aria-live="polite"
            >
              <span className={styles.addGuestsToastCheck}>
                <SuccessIcon />
              </span>
              <span className={styles.addGuestsToastBody}>
                {t("eventDashboardV2.messages.guestAddedToEvent", { name: lastAdded.name })}
              </span>
            </div>
          )}

          <p className={styles.addGuestsHint}>
            {t("eventDashboardV2.messages.addGuestsHint")}
          </p>

          <form className={styles.drawerForm} onSubmit={(e) => { e.preventDefault(); handleAddGuest() }}>
            <div className={styles.drawerSection}>
              <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.labels.guestInformation")}</p>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.title")}</label>
                <CreatableSelect
                  options={suffixOptions}
                  value={
                    guest.suffix
                      ? suffixOptions.find((o) => o.value === guest.suffix) ?? {
                          value: guest.suffix,
                          label: guest.suffix,
                        }
                      : null
                  }
                  onChange={(val) => handleInput("suffix", val?.value || "")}
                  onCreateOption={(input) => handleInput("suffix", input.trim())}
                  placeholder={t("eventDashboardV2.messages.selectOrTypeTitle")}
                  formatCreateLabel={(input) => t("eventDashboardV2.messages.useTitle", { title: input })}
                  styles={selectStyles}
                  isClearable
                />
              </div>

              <div className={styles.drawerFieldRow}>
                <div className={styles.drawerField}>
                  <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.firstName")} *</label>
                  <input
                    ref={firstNameRef}
                    type="text"
                    className={`${styles.drawerInput} ${validation.first_name ? styles.drawerInputError : ""}`}
                    value={guest.first_name}
                    onChange={(e) => handleInput("first_name", e.target.value)}
                  />
                  {validation.first_name && (
                    <span className={styles.drawerFieldError}>{validation.first_name}</span>
                  )}
                </div>
                <div className={styles.drawerField}>
                  <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.lastName")}</label>
                  <input
                    type="text"
                    className={`${styles.drawerInput} ${validation.last_name ? styles.drawerInputError : ""}`}
                    value={guest.last_name}
                    onChange={(e) => handleInput("last_name", e.target.value)}
                  />
                  {validation.last_name && (
                    <span className={styles.drawerFieldError}>{validation.last_name}</span>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.drawerSection}>
              <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.table.contact")}</p>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.phoneNumber")} *</label>
                <div className={`${styles.phoneInputWrap} ${validation.phone ? styles.phoneInputError : ""}`}>
                  <PhoneInput
                    country="sa"
                    preferredCountries={GULF_COUNTRIES}
                    enableSearch
                    disableSearchIcon
                    value={guest.phone}
                    onChange={handlePhoneChange}
                    inputProps={{ autoComplete: "tel" }}
                    searchPlaceholder={t("eventDashboardV2.messages.searchCountries")}
                    isValid={(value) => {
                      if (!value) return true
                      try {
                        const parsed = parsePhoneNumberFromString(toE164(value))
                        return !parsed || parsed.isPossible()
                      } catch {
                        return true
                      }
                    }}
                  />
                </div>
                {validation.phone && (
                  <span className={styles.drawerFieldError}>{validation.phone}</span>
                )}
              </div>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.email")}</label>
                <input
                  type="text"
                  className={`${styles.drawerInput} ${validation.email ? styles.drawerInputError : ""}`}
                  value={guest.email}
                  onChange={(e) => handleInput("email", e.target.value)}
                />
                {validation.email && (
                  <span className={styles.drawerFieldError}>{validation.email}</span>
                )}
              </div>
            </div>

            <div className={styles.drawerSection}>
              <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.labels.eventDetails")}</p>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.zone")}</label>
                <input
                  type="text"
                  className={styles.drawerInput}
                  value={guest.zone}
                  onChange={(e) => handleInput("zone", e.target.value)}
                  placeholder={t("eventDashboardV2.messages.optional")}
                />
              </div>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.additionalInvites")}</label>
                <input
                  type="number"
                  className={`${styles.drawerInput} ${validation.quantity_members ? styles.drawerInputError : ""}`}
                  value={guest.quantity_members}
                  onChange={(e) => handleInput("quantity_members", e.target.value)}
                />
                <span className={styles.drawerFieldHint}>{t("eventDashboardV2.messages.numberOfAdditionalGuests")}</span>
                {validation.quantity_members && (
                  <span className={styles.drawerFieldError}>{validation.quantity_members}</span>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ---- Import Tab ---- */}
      {activeTab === "import" && (
        <div className={styles.addGuestsContent}>
          <p className={styles.addGuestsHint}>
            {t("eventDashboardV2.messages.importHint")}
          </p>

          <div className={styles.importSection}>
            <ChooseFileImport
              progress={importProgress}
              setProgress={setImportProgress}
              file={file}
              setFile={setFile}
              goToEvent={handleImportDone}
            />
          </div>

          {!file && (
            <div className={styles.importTemplateArea}>
              <div className={styles.importTemplateHeader}>
                <FileIcon />
                <span>{t("eventDashboardV2.messages.templateFiles")}</span>
              </div>
              <p className={styles.importTemplateText}>
                {t("eventDashboardV2.messages.templateHelp")}
              </p>
              <div className={styles.importTemplateActions}>
                <a href="./files/import_guests_example.xlsx" target="_blank" rel="noreferrer">
                  <button type="button" className={styles.importTemplateBtn}>
                    <DownloadIcon /> {t("eventDashboardV2.messages.exampleFile")}
                  </button>
                </a>
                <button type="button" className={styles.importTemplateBtn} onClick={downloadTemplate}>
                  <DownloadIcon /> {t("eventDashboardV2.messages.blankTemplate")}
                </button>
              </div>
            </div>
          )}

          {importDone && (
            <div className={styles.addGuestsSuccess}>
              <SuccessIcon />
              {t("eventDashboardV2.messages.importComplete")}
            </div>
          )}
        </div>
      )}

      {/* ---- Contacts Tab ---- */}
      {activeTab === "contacts" && (
        <div className={styles.contactsPane}>
          {selCount > 0 && (
            <div className={styles.contactsSelectionBar}>
              <span className={styles.contactsSelBadge}>{selCount}</span>
              <span>{t("eventDashboardV2.messages.contactsSelected", { count: selCount })}</span>
              <button
                type="button"
                className={styles.contactsSelClear}
                onClick={() => setSelectedContactIds(new Set())}
              >
                {t("eventDashboardV2.actions.clear")}
              </button>
            </div>
          )}

          <div className={styles.contactsSearchWrap}>
            <SearchIcon />
            <input
              type="text"
              placeholder={t("eventDashboardV2.messages.searchContacts")}
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
            />
            {contactSearch && (
              <button
                type="button"
                className={styles.contactsSearchClear}
                onClick={() => setContactSearch("")}
              >
                <CloseSmallIcon />
              </button>
            )}
          </div>

          {loadingContacts ? (
            <div className={styles.contactsRows}>
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className={styles.contactsSkeletonRow}>
                  <div className={`${styles.skeleton} ${styles.contactsSkeletonAvatar}`} />
                  <div className={styles.contactsSkeletonText}>
                    <div className={`${styles.skeleton} ${styles.contactsSkeletonName}`} />
                    <div className={`${styles.skeleton} ${styles.contactsSkeletonPhone}`} />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className={styles.contactsEmptyState}>
              <BookIcon />
              <p>{contactSearch ? t("eventDashboardV2.messages.noContactsMatch") : t("eventDashboardV2.messages.noContactsInAddressBook")}</p>
            </div>
          ) : (
            <>
              <label className={styles.contactsSelectAll}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectableContacts.length > 0 && selCount === selectableContacts.length}
                  disabled={selectableContacts.length === 0}
                  onChange={toggleAllContacts}
                />
                <span>{t("eventDashboardV2.messages.selectAll")}</span>
                <span className={styles.contactsCount}>{selectableContacts.length}</span>
              </label>

              <div className={styles.contactsRows}>
                {filteredContacts.map((contact: any) => {
                  const alreadyAdded = isOnEvent(contact)
                  const isSelected = selectedContactIds.has(contact.id)
                  const name = [contact.suffix, contact.first_name, contact.last_name].filter(Boolean).join(" ") || t("eventDashboardV2.labels.unnamed")
                  const phone = formatContactPhone(contact.phone || "")
                  const email = contact.email || ""

                  return (
                    <label
                      key={contact.id}
                      className={`${styles.contactsRow} ${isSelected ? styles.contactsRowSelected : ""} ${alreadyAdded ? styles.contactsRowDisabled : ""}`}
                    >
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={isSelected}
                        disabled={alreadyAdded}
                        onChange={() => toggleContact(contact.id)}
                      />
                      <div className={`${styles.contactsAvatar} ${isSelected ? styles.contactsAvatarSelected : ""}`}>
                        {getInitials(contact)}
                      </div>
                      <div className={styles.contactsRowInfo}>
                        <span className={styles.contactsRowName}>{name}</span>
                        <span className={styles.contactsRowDetail}>
                          {alreadyAdded
                            ? t("eventDashboardV2.messages.alreadyOnGuestList")
                            : (
                              <>
                                {phone || email || t("eventDashboardV2.messages.noContactInfo")}
                                {phone && email && <> &middot; {email}</>}
                              </>
                            )}
                        </span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}
    </Drawer>
  )
}

// ---- Inline icons ----
const SuccessIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 1C4.13 1 1 4.13 1 8s3.13 7 7 7 7-3.13 7-7-3.13-7-7-7zm3.41 5.41-4 4a.58.58 0 0 1-.82 0l-2-2a.58.58 0 1 1 .82-.82L7 9.17l3.59-3.58a.58.58 0 1 1 .82.82z"
      fill="currentColor"
    />
  </svg>
)

const SpinnerIcon = () => (
  <svg
    className={styles.spinIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="14"
    height="14"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
)

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const PenIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const BookIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
)

const FileIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
)

const CloseSmallIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

export default AddGuestsDrawer
